import type { Workspace } from '@protoss/shared';

/**
 * useWorkspaceAdmin - check if the current user is a workspace admin
 *
 * Returns true if the user has workspace admin privileges (isAdmin=true in workspace_user table).
 *
 * SECURITY POLICY:
 * - Never use localStorage for authorization (see context/local-storage.md)
 * - Always read from backend-provided workspace data
 * - localStorage is untrusted client-side storage and must not control access
 *
 * Backend implementation:
 * - GET /api/workspace/current returns isAdmin field
 * - The workspace service queries workspace_user.isAdmin
 * - Implementation in apps/api/src/services/workspace.ts
 *
 * Example usage:
 * ```tsx
 * const [workspace, setWorkspace] = useState<Workspace | null>(null);
 * const isAdmin = useWorkspaceAdmin(workspace);
 *
 * return (
 *   <>
 *     {isAdmin && <AdminPanel />}
 *   </>
 * );
 * ```
 */
export function useWorkspaceAdmin(workspace: Workspace | null | undefined): boolean {
  // Read from workspace data provided by backend API
  // Default to false if workspace not loaded or isAdmin not set
  return workspace?.isAdmin ?? false;
}
